import React, { useEffect, useMemo, useState } from "react";
import { CalendarDays, DownloadIcon } from "lucide-react";
import CustomModal from "../../../../Shared/CustomModal";
import styles from "./FrequencyBucketTable.module.css";
import TablePagination from "./TablePagination";
import VehicleMultiSelect, {
    filterBySelectedVehicles,
    formatPopoverDate,
    getRegNumber,
    toVehicleOptions
} from "./VehicleMultiSelect";
import { useDeviceHealthContextData } from "./context/Context";
import { getHealthyUnhealthyVehicles } from "./DeviceHealthService";
import { downloadTCUKpis } from "../../../../../services/cesl-bus/tcuService";

const ROWS_PER_PAGE = 10; // fixed, not user-changeable
const COL_COUNT = 7;

const displayValue = (val) =>
    val === null || val === undefined || val === "" ? "-" : val;

function StatusBadge({ frequency }) {
    const isHealthy = Number(frequency) > 80;
    return (
        <span className={`${styles.statusBadge} ${isHealthy ? styles.statusHealthy : styles.statusUnhealthy}`}>
        </span>
    );
}

/*
 * status: "healthy" | "unhealthy" — set when a Health Status KPI bucket is clicked.
 * Same layout as DepotVehiclesPopover, without the city / depot badge.
 */
export default function HealthyUnhealthyPopover({ status }) {
    const { GlobalFilter, lastRefreshDate } = useDeviceHealthContextData();
    const [vehicles, setVehicles] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedVehicles, setSelectedVehicles] = useState([]);

    useEffect(() => {
        if (!status) return;

        let cancelled = false;
        setIsLoading(true);
        setCurrentPage(1);
        setSelectedVehicles([]);

        getHealthyUnhealthyVehicles({ ...GlobalFilter, status })
            .then((res) => {
                if (!cancelled) setVehicles(res?.vehicles || []);
            })
            .catch(() => {
                if (!cancelled) setVehicles([]);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [GlobalFilter, status]);

    const vehicleOptions = useMemo(() => toVehicleOptions(vehicles), [vehicles]);

    const filteredVehicles = useMemo(
        () => filterBySelectedVehicles(vehicles, selectedVehicles),
        [vehicles, selectedVehicles]
    );

    const changeSelectedVehicles = (value) => {
        setSelectedVehicles(value);
        setCurrentPage(1);
    };

    // same CSV api as the Health Status KPI, limited to the opened status
    const downloadVehicles = () => {
        downloadTCUKpis("downloadTotalAvailableDeviceHealth", { ...GlobalFilter, status })
            .then((data) => {
                const url = window.URL.createObjectURL(data);

                const link = document.createElement("a");
                link.href = url;
                link.download = `${status} vehicles.csv`;

                document.body.appendChild(link);
                link.click();
                link.remove();

                window.URL.revokeObjectURL(url);
            })
            .catch(() => { });
    };

    const totalItems = filteredVehicles.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedVehicles = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return filteredVehicles.slice(start, start + ROWS_PER_PAGE);
    }, [filteredVehicles, safePage]);

    return (
        <CustomModal
            id="HealthyUnhealthyPopover"
            title={`${status} vehicles`}
            size="modal-xl"
            header={true}
            closeBtnClass="btn ms_btn btn-default"
        >
            <div className={styles.popoverBody}>
                <div className={styles.popoverMeta}>
                    <span className={styles.metaChip}>
                        <CalendarDays size={11} />
                        {formatPopoverDate(lastRefreshDate)}
                    </span>
                    <VehicleMultiSelect
                        options={vehicleOptions}
                        value={selectedVehicles}
                        onChange={changeSelectedVehicles}
                    />
                    <span className={styles.metaCount}>
                        {isLoading
                            ? "Loading..."
                            : selectedVehicles.length
                                ? `${totalItems} of ${vehicles.length} vehicles`
                                : `${totalItems} vehicles`}
                    </span>
                    <span
                        className={`${styles.metaDownload} custom-Btn-animation`}
                        title={`Download ${status} vehicles`}
                        onClick={downloadVehicles}
                    >
                        <DownloadIcon size={14} />
                    </span>
                </div>

                <div className={styles.popoverTableWrapper}>
                    <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable} ${styles.popoverTable}`}>
                        <colgroup>
                            <col className={styles.colVin} />
                            <col className={styles.colReg} />
                            <col />
                            <col />
                            <col className={styles.colStatus} />
                            <col className={styles.colFreq} />
                            <col className={styles.colFreq} />
                        </colgroup>

                        <thead className="sticky-top">
                            <tr>
                                <th>VIN No.</th>
                                <th>Reg No.</th>
                                <th>Depot</th>
                                <th>Contract</th>
                                <th>Status</th>
                                <th>Freq%</th>
                                <th>Avg Delay</th>
                            </tr>
                        </thead>

                        <tbody>
                            {isLoading ? (
                                <tr>
                                    <td colSpan={COL_COUNT}>
                                        <div className={styles.loadingState}>
                                            <span className={styles.spinner} />
                                            <span>Loading...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : totalItems === 0 ? (
                                <tr>
                                    <td colSpan={COL_COUNT}>
                                        <div className={styles.loadingState}>
                                            {vehicles.length ? "No vehicles match your selection" : "No vehicles found"}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                paginatedVehicles.map((v, idx) => (
                                    <tr key={v.vin || idx} className={styles.locationRow}>
                                        <td className={styles.cityCell}>
                                            <span className={styles.vinText} title={v.vin}>{displayValue(v.vin)}</span>
                                        </td>
                                        <td>{displayValue(getRegNumber(v))}</td>
                                        <td>{displayValue(v.Depot_Name ?? v.Depot)}</td>
                                        <td>{displayValue(v.Contract)}</td>
                                        <td><StatusBadge frequency={v.frequency} /></td>
                                        <td>{displayValue(v.frequency)}</td>
                                        <td>{displayValue(v.avg_delay)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {!isLoading && (
                    <TablePagination
                        page={safePage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        pageSize={ROWS_PER_PAGE}
                        onPageChange={setCurrentPage}
                    />
                )}
            </div>
        </CustomModal>
    );
}
